import { ArrowUpIcon } from '~components/icons/ArrowUpIcon';
import { ClipboardAddIcon } from '~components/icons/ClipboardAddIcon';
import { CollectionIcon } from '~components/icons/CollectionIcon';
import { CropIcon } from '~components/icons/CropIcon';
import { DownloadIcon } from '~components/icons/DownloadIcon';
import { FolderSymlinkIcon } from '~components/icons/FolderSymlinkIcon';
import { LinkIcon } from '~components/icons/LinkIcon';
import { MagnifyingGlassIcon } from '~components/icons/MagnifyingGlassIcon';
import { PenIcon } from '~components/icons/PenIcon';
import { PlayIcon } from '~components/icons/PlayIcon';
import useAppSelector from '~hooks/useAppSelector';
import useLabels from '~hooks/useLabels';
import usePermissions from '~hooks/usePermissions';
import { Asset } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { isAssetCollection, isAssetVideo, isCanvaTemplate } from '~modules/media-library/model/AssetFactory';
import { AssetMenu } from '~modules/media-library/view/AssetMenu/AssetMenu';
import { Button } from '~modules/media-library/view/Button';
import { selectAddonsIsWeb2PrintMaintenance } from '~store/selectors';

type PanelActionsProps = {
  asset: Asset;
  AssetActions: AssetActions;
};

export function PanelActions({ asset, AssetActions }: PanelActionsProps) {
  const {
    btnMediaLibraryActionDownload,
    btnMediaLibraryActionUseWeb2Print,
    btnMediaLibraryActionUseCanva,
    btnMediaLibraryActionCopyId,
    btnMediaLibraryActionCopyLink,
    btnMediaLibraryActionCropImage,
    btnMediaLibraryActionZoom,
    btnMediaLibraryActionOpenCollection,
    btnMediaLibraryActionPlay,
    btnMediaLibraryActionAddToMyfiles,
    btnMediaLibraryActionJsmContactForm,
  } = useLabels([
    'btnMediaLibraryActionDownload',
    'btnMediaLibraryActionUseWeb2Print',
    'btnMediaLibraryActionUseCanva',
    'btnMediaLibraryActionCopyId',
    'btnMediaLibraryActionCopyLink',
    'btnMediaLibraryActionCropImage',
    'btnMediaLibraryActionZoom',
    'btnMediaLibraryActionOpenCollection',
    'btnMediaLibraryActionPlay',
    'btnMediaLibraryActionAddToMyfiles',
    'btnMediaLibraryActionJsmContactForm',
  ]);

  const isWeb2PrintMaintenance = useAppSelector(selectAddonsIsWeb2PrintMaintenance);
  const { hasPassMaintenanceModePermission } = usePermissions();

  const isCollection: boolean = isAssetCollection(asset);

  const onCollection = isCollection ? AssetActions.onCollection : undefined;
  const onPlay = isAssetVideo(asset) && Boolean(asset.attributes.videoPreview) ? AssetActions.onPlay : undefined;
  const onCopyLink = AssetActions.onCopyLink;
  // disable copy id - NEOBS-898
  const onCopyId = undefined; //asset.actions.copyId ? AssetActions.onCopyId : undefined;
  const onUseWeb2Print =
    asset.actions.web2Print && (!isWeb2PrintMaintenance || hasPassMaintenanceModePermission)
      ? AssetActions.onUseWeb2Print
      : undefined;
  // all user can see the button and in the modal it will show a message if user has no permissions
  const onUseCanva = isCanvaTemplate(asset) ? AssetActions.onUseCanva : undefined;
  const onCrop = !!asset.actions.crop ? AssetActions.onCrop : undefined;
  const onZoom = !!asset.previews.zoom ? AssetActions.onZoom : undefined;
  const onDownload = asset.actions.download ? AssetActions.onDownload : undefined;
  const onAddToMyfiles = asset.actions.myFiles ? AssetActions.onAddToMyfiles : undefined;
  const edit = asset.actions.edit;
  const onJsmContactForm = asset.actions.jsmContactForm ? AssetActions.onJsmContactForm : undefined;
  const editPermissions = asset.actions.editPermissions;
  const raiseRequest = asset.actions.raiseRequest;
  const approve = asset.actions.approve;
  const restartContribution = asset.actions.restartContribution;
  const deleteAllowed = asset.actions.delete;

  return (
    <div className="ml--detail-modal--panel-actions flex gap-2 px-3 py-2 bg-gray justify-between align-items-center">
      <div className="justify-between flex">
        {!!onCollection ? (
          <Button
            className="ml-icon-collection "
            onClick={() => onCollection?.(asset.id)}
            title={btnMediaLibraryActionOpenCollection}
          >
            <CollectionIcon className="mr-2 -ml-1" />
            {btnMediaLibraryActionOpenCollection}
          </Button>
        ) : null}
        {!!onPlay ? (
          <Button
            variant="icon"
            className="ml-icon-play"
            onClick={() => onPlay?.(asset.id)}
            title={btnMediaLibraryActionPlay}
          >
            <PlayIcon />
          </Button>
        ) : null}
        {!!onDownload ? (
          <Button
            variant="icon"
            className="ml-icon-download"
            onClick={() => onDownload?.(asset.id)}
            title={btnMediaLibraryActionDownload}
          >
            <DownloadIcon />
          </Button>
        ) : null}
        {!!onAddToMyfiles ? (
          <Button
            variant="icon"
            className="ml-icon-symlink  ml-2 "
            onClick={() => onAddToMyfiles?.(asset.id)}
            title={btnMediaLibraryActionAddToMyfiles}
          >
            <FolderSymlinkIcon />
          </Button>
        ) : null}
        {!!onUseWeb2Print ? (
          <Button
            variant="icon"
            className="ml-icon-arrow-diagonally"
            onClick={() => onUseWeb2Print?.(asset.id)}
            title={btnMediaLibraryActionUseWeb2Print}
          >
            <ArrowUpIcon className="rotate-45 transform" />
          </Button>
        ) : null}
        {!!onUseCanva ? (
          <Button
            variant="icon"
            className="ml-icon-arrow-diagonally"
            onClick={() => onUseCanva?.(asset.id)}
            title={btnMediaLibraryActionUseCanva}
          >
            <ArrowUpIcon className="rotate-45 transform" />
          </Button>
        ) : null}
        {!!onCrop ? (
          <Button
            variant="icon"
            className="ml-icon-crop ml-2 "
            onClick={() => onCrop?.(asset.id)}
            title={btnMediaLibraryActionCropImage}
          >
            <CropIcon />
          </Button>
        ) : null}
        {!!onZoom && !isCollection ? (
          <Button
            variant="icon"
            className="ml-icon-zoom ml-2 "
            onClick={() => onZoom?.(asset.id)}
            title={btnMediaLibraryActionZoom}
          >
            <MagnifyingGlassIcon />
          </Button>
        ) : null}
        {!!onJsmContactForm ? (
          <Button
            variant="icon"
            className="ml-icon-clipboard ml-2 "
            onClick={() => onJsmContactForm?.(asset.id)}
            title={btnMediaLibraryActionJsmContactForm.replace('<br>', '')}
          >
            <PenIcon />
          </Button>
        ) : null}
        {!!onCopyLink ? (
          <Button
            variant="icon"
            className="ml-icon-link ml-2 "
            onClick={() => onCopyLink?.(asset.id)}
            title={btnMediaLibraryActionCopyLink}
          >
            <LinkIcon />
          </Button>
        ) : null}
        {!!onCopyId ? (
          <Button
            variant="icon"
            className="ml-icon-clipboard ml-2 "
            onClick={() => onCopyId?.(asset.id)}
            title={btnMediaLibraryActionCopyId}
          >
            <ClipboardAddIcon />
          </Button>
        ) : null}
        {!!approve ? (
          <Button variant="default" className="ml-2" onClick={() => AssetActions.onApprovalForm(asset.id)}>
            Approval
          </Button>
        ) : null}
        {!!restartContribution ? (
          <Button variant="default" className="ml-2" onClick={() => AssetActions.onRestartContribution(asset.id)}>
            Restart Contribution
          </Button>
        ) : null}
      </div>
      {edit || editPermissions || raiseRequest || deleteAllowed ? (
        <AssetMenu
          assetId={asset.id}
          edit={edit}
          editPermissions={editPermissions}
          raiseRequest={raiseRequest}
          deleteAllowed={deleteAllowed}
          AssetActions={AssetActions}
        />
      ) : null}
    </div>
  );
}
